import { AccountTreeOutlined, ArrowForwardOutlined, BadgeOutlined, ContactPhoneOutlined, HistoryOutlined, MailOutlined } from '@mui/icons-material';
import { Alert, Avatar, Box, Button, Stack, Typography } from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import type {
  PersonAddress,
  PersonNameProfile,
  PersonPhoneNumber,
  StaffingChangeSummary,
  WorkdayReference,
  WorkerEmploymentProfile,
  WorkerPrivateProfile,
  WorkerProfileWorker,
} from './model';
import { IdentityAvatar, CompactEditButton, DefinitionGrid, PillTabs, SectionCard, StatusBadge, ValueBadge, designTokens, type PillTabItem } from '../../../design-system';
import { profileDateValue, profileTextValue } from './profileDisplay';
import { workerStatusPresentation } from './workerStatusPresentation';
import { formatPersonDisplayName } from '../../../shared/formatPersonDisplayName';

type WorkerProfileProps = {
  worker: WorkerProfileWorker;
  asOf: string;
  nameProfile?: PersonNameProfile;
  nameState?: 'loading' | 'ready' | 'forbidden' | 'error' | 'unavailable';
  privateLoading?: boolean;
  employmentLoading?: boolean;
  employmentError?: boolean;
  changes?: StaffingChangeSummary[];
  photoControl?: ReactNode;
  familyContent?: ReactNode;
  careerContent?: ReactNode;
  historyContent?: ReactNode;
  onEditName?: () => void;
  onEditContact?: (section?: 'HOME' | 'WORK' | 'EMERGENCY') => void;
  onStartStaffing?: () => void;
  personnelActions?: ReactNode;
  onOpenOrganizationStaffing?: () => void;
  privateProfile?: WorkerPrivateProfile;
  employmentProfile?: WorkerEmploymentProfile;
};

const processLabels: Record<string, string> = {
  HIRE: '입사', JOB_CHANGE: '소속·직무 변경', LEAVE_OF_ABSENCE: '휴직', RETURN_FROM_LEAVE: '복직', END_WORKER: '퇴직·계약 종료',
};

type WorkerProfileTab = 'overview' | 'employment' | 'family' | 'career' | 'history';

function dateTone(date: string, asOf: string) {
  if (date > asOf) return { label: '예정', color: 'info' as const };
  return { label: '적용', color: 'success' as const };
}

function profileSectionTitle(testId: string, icon: ReactNode, title: ReactNode) {
  return <Stack component="h3" data-testid={testId} direction="row" spacing={1} sx={{ m: 0, font: 'inherit', alignItems: 'center' }}>
    {icon}
    <Box component="span">{title}</Box>
  </Stack>;
}

function categoryValue(value?: string) {
  const label = profileTextValue(value);
  return label ? <ValueBadge label={label} /> : undefined;
}

function unavailableValue(label: string) {
  return <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>{label}</Box>;
}

function privateFieldValue(canRead: boolean, value?: string, category = false, loaded?: boolean, loading = false) {
  if (!canRead) return unavailableValue('조회 권한 없음');
  if (loading) return unavailableValue('불러오는 중');
  if (loaded === false) return unavailableValue('불러오기 실패');
  return category ? categoryValue(value) : profileTextValue(value);
}

function ProfileGroup({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return <Box component="section" aria-label={title} sx={{ minWidth: 0 }}>
    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 1.5 }}>
      <Typography component="h4" variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Box>;
}

function primaryItem<T extends { primary?: boolean }>(items: T[]) {
  return items.find((item) => item.primary) ?? items[0];
}

const genderLabels: Record<string, string> = {
  MALE: '남성',
  FEMALE: '여성',
};

const maritalStatusLabels: Record<string, string> = {
  SINGLE: '미혼',
  MARRIED: '기혼',
  DIVORCED: '이혼',
  WIDOWED: '사별',
  SEPARATED: '별거',
};

const phoneTypeLabels: Record<string, string> = {
  HOME: '자택',
  MOBILE: '휴대전화',
  WORK: '업무',
  FAX: '팩스',
  OTHER: '기타',
};

const emergencyRelationshipLabels: Record<string, string> = {
  SPOUSE: '배우자', PARTNER: '파트너', PARENT: '부모', SIBLING: '형제·자매', CHILD: '자녀',
  GUARDIAN: '보호자', FRIEND: '친구', COLLEAGUE: '동료', OTHER: '기타',
};

const contractTypeLabels: Record<string, string> = {
  PERMANENT: '무기계약',
  FIXED_TERM: '기간제 계약',
  CONTINGENT: '비정규 계약',
  INTERN: '인턴 계약',
  OTHER: '기타',
};

const workerTypeLabels: Record<string, string> = {
  EMPLOYEE: '임직원',
  CONTINGENT_WORKER: '외부 인력',
};

const timeTypeLabels: Record<NonNullable<WorkerProfileWorker['timeType']>, string> = {
  FULL_TIME: '전일제',
  PART_TIME: '시간제',
};

function genderValue(gender?: WorkdayReference) {
  if (!gender) return undefined;
  const code = gender.id.split('/').at(-1)?.toUpperCase() ?? gender.id.toUpperCase();
  return genderLabels[code] || gender.descriptor || gender.id;
}

function maritalStatusValue(maritalStatus?: WorkdayReference) {
  if (!maritalStatus) return undefined;
  const code = maritalStatus.id.split('/').at(-1)?.toUpperCase() ?? maritalStatus.id.toUpperCase();
  return maritalStatusLabels[code] || maritalStatus.descriptor || maritalStatus.id;
}

function workerTypeValue(workerType: string, workerTypeId?: string) {
  for (const value of [workerTypeId, workerType]) {
    const code = value?.split('/').at(-1)?.trim().replace(/[\s-]+/g, '_').toUpperCase();
    if (code && workerTypeLabels[code]) return workerTypeLabels[code];
  }
  return workerType;
}

function addressValue(address?: PersonAddress) {
  if (!address) return undefined;
  if (address.descriptor && !address.addressLines?.length) return address.descriptor;
  return [address.region, address.city, ...(address.addressLines ?? []), address.postalCode].filter(Boolean).join(' ');
}

function phoneLabel(scope: 'work' | 'home', phone: PersonPhoneNumber, index: number) {
  const prefix = scope === 'work' ? '회사 전화' : '개인 전화';
  const type = phoneTypeLabels[phone.usageType ?? ''] || phone.usageType;
  return [prefix, type, phone.primary ? '대표' : undefined].filter(Boolean).join(' · ') || `${prefix} ${index + 1}`;
}

function phoneValue(phone: PersonPhoneNumber) {
  return profileTextValue(phone.phoneNumber || phone.descriptor);
}

function contactVisibilityValue(value: string | undefined, isPublic: boolean | undefined) {
  if (!value || isPublic === undefined) return value;
  return <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
    <Box component="span" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{value}</Box>
    <ValueBadge label={isPublic ? '공개' : '비공개'} />
  </Stack>;
}

function emergencyRelationshipValue(value?: string) {
  if (!value) return '관계 미등록';
  const code = value.split('/').at(-1)?.trim().toUpperCase() ?? value.toUpperCase();
  return emergencyRelationshipLabels[code] || value;
}

export function WorkerProfile({
  worker,
  asOf,
  nameProfile,
  nameState = 'ready',
  privateLoading = false,
  employmentLoading = false,
  employmentError = false,
  changes = [],
  photoControl,
  familyContent,
  careerContent,
  historyContent,
  onEditName,
  onEditContact,
  onStartStaffing,
  personnelActions,
  onOpenOrganizationStaffing,
  privateProfile,
  employmentProfile,
}: WorkerProfileProps) {
  const [tab, setTab] = useState<WorkerProfileTab>('overview');
  const workerChanges = useMemo(() => changes.filter((change) => {
    const ids = [change.worker?.id, change.person?.id, change.subject.id].filter((id): id is string => Boolean(id));
    if (ids.length) return ids.includes(worker.id) || ids.includes(worker.personId);
    const names = [change.worker?.descriptor, change.person?.descriptor, change.subject.descriptor];
    return names.includes(worker.name);
  }), [changes, worker.id, worker.name, worker.personId]);
  const profileTabs: PillTabItem<WorkerProfileTab>[] = [
    { value: 'overview', label: <Box component="span" data-testid="worker-profile-overview-tab-label">프로필</Box>, tabId: 'worker-profile-overview-tab', panelId: 'worker-profile-overview-panel' },
    { value: 'employment', label: '인사 정보', tabId: 'worker-profile-employment-tab', panelId: 'worker-profile-employment-panel' },
  ];
  if (familyContent) profileTabs.push({ value: 'family', label: '가족', tabId: 'worker-profile-family-tab', panelId: 'worker-profile-family-panel' });
  if (careerContent) profileTabs.push({ value: 'career', label: '경력', tabId: 'worker-profile-career-tab', panelId: 'worker-profile-career-panel' });
  profileTabs.push({ value: 'history', label: historyContent ? '변경 이력' : `변경 이력 ${workerChanges.length}`, tabId: 'worker-profile-history-tab', panelId: 'worker-profile-history-panel' });
  const legalName = nameProfile?.legalName.fullName.trim();
  const preferredName = nameProfile?.preferredName?.fullName.trim();
  const displayName = formatPersonDisplayName(preferredName, legalName, worker.name);
  const jobName = worker.jobProfile || worker.title;
  const workerTypeDisplay = workerTypeValue(worker.workerType, worker.workerTypeId);
  const isEmployee = workerTypeDisplay === '임직원';
  const jobTypeLabel = isEmployee ? '고용 유형' : '외부 인력 유형';
  const personalEmail = primaryItem(privateProfile?.homeEmails ?? [])?.emailAddress;
  const workEmail = primaryItem(privateProfile?.workEmails ?? []);
  const workVisibilityLoaded = privateProfile?.canReadWorkContactFull && privateProfile.workContactLoaded;
  const homeAddress = primaryItem(privateProfile?.homeAddresses ?? []);
  const workPhones: PersonPhoneNumber[] = privateProfile?.canReadWorkContact && privateProfile.workPhones?.length
    ? privateProfile.workPhones
    : [{ id: 'worker-summary-phone', phoneNumber: worker.phone, usageType: 'WORK', primary: true }];
  const homePhones = privateProfile?.homePhones ?? [];
  const nameValue = (value?: string) => nameState === 'ready' ? profileTextValue(value) : unavailableValue({
    loading: '불러오는 중', forbidden: '조회 권한 없음', error: '불러오기 실패', unavailable: '이름 원장 없음',
  }[nameState]);
  const homeValue = (value: string | undefined, loaded: boolean) => privateFieldValue(Boolean(privateProfile?.canReadHomeContact), value, false, loaded, privateLoading);
  const workValue = (value: ReactNode, loaded: boolean) => !privateProfile?.canReadWorkContact
    ? unavailableValue('조회 권한 없음')
    : privateLoading ? unavailableValue('불러오는 중')
    : !loaded ? unavailableValue('불러오기 실패') : value;
  const workContactItems = [
    { id: 'work-email', label: '회사 이메일', value: workValue(contactVisibilityValue(profileTextValue(workEmail?.emailAddress || worker.email), workVisibilityLoaded && workEmail ? workEmail.public === true : undefined), Boolean(privateProfile?.workContactLoaded || privateProfile?.workEmails !== undefined)) },
    ...workPhones.map((phone, index) => ({ id: `work-phone-${index}`, label: phoneLabel('work', phone, index), value: workValue(contactVisibilityValue(phoneValue(phone), workVisibilityLoaded && phone.id !== 'worker-summary-phone' ? phone.public === true : undefined), Boolean(privateProfile?.workContactLoaded || privateProfile?.workPhones !== undefined)) })),
  ];
  const homeContactItems = [
    { id: 'personal-email', label: '개인 이메일', value: homeValue(personalEmail, Boolean(privateProfile?.homeContactLoaded || privateProfile?.homeEmails !== undefined)) },
    ...(homePhones.length
      ? homePhones.map((phone, index) => ({ id: `home-phone-${index}`, label: phoneLabel('home', phone, index), value: homeValue(phoneValue(phone), Boolean(privateProfile?.homeContactLoaded || privateProfile?.homePhones !== undefined)) }))
      : [{ id: 'home-phone-empty', label: '개인 전화', value: homeValue(undefined, Boolean(privateProfile?.homeContactLoaded || privateProfile?.homePhones !== undefined)) }]),
    { id: 'home-address', label: '집 주소', value: homeValue(addressValue(homeAddress), Boolean(privateProfile?.homeContactLoaded || privateProfile?.homeAddresses !== undefined)) },
  ];
  const canEditHome = onEditContact && privateProfile?.canReadHomeContactFull && privateProfile.homeContactLoaded;
  const canEditWork = onEditContact && privateProfile?.canReadWorkContactFull && privateProfile.workContactLoaded;
  const canReadEmergency = privateProfile?.canReadEmergencyContact ?? privateProfile?.canReadHomeContact;
  const canEditEmergency = canEditHome && canReadEmergency && privateProfile?.emergencyContactsLoaded;
  const summaryEmail = privateProfile?.canReadWorkContact && !privateLoading
    && (privateProfile.workContactLoaded || privateProfile.workEmails !== undefined)
    ? profileTextValue(workEmail?.emailAddress || worker.email) : undefined;
  const summaryItems = [
    { label: '사번', value: profileTextValue(worker.workerNumber) || '미등록' },
    { label: '최근 입사일', value: profileDateValue(worker.hireDate) || '미입력' },
    { label: '근무지', value: profileTextValue(worker.location) || '미입력' },
  ];

  return <Stack spacing={2}>
    <Box component="section" aria-label={`${displayName} 프로필`} sx={{ minWidth: 0 }}>
      <SectionCard
        headerPadding="standard"
        showDivider={false}
        children={null}
        title={<Box sx={{
          display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.15fr) minmax(0, 1fr)' },
          gap: { xs: 2, md: 3 }, alignItems: 'center',
        }}>
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} sx={{ alignItems: 'center', minWidth: 0 }}>
            {photoControl || <IdentityAvatar src={worker.photoUrl} alt={`${worker.name} 프로필 사진`} sx={{ width: { xs: 64, sm: 92 }, height: { xs: 64, sm: 92 }, flexShrink: 0, fontSize: 28, fontWeight: 700, border: `3px solid ${designTokens.color.canvas}`, boxShadow: `0 0 0 1px ${designTokens.color.strongDivider}` }}>{worker.initials}</IdentityAvatar>}
            <Box data-testid="worker-profile-identity" sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography component="h2" variant="h5" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{displayName}</Typography>
                <StatusBadge {...workerStatusPresentation(worker.status)} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, overflowWrap: 'anywhere' }}>
                <Box component="span" sx={{ color: designTokens.color.primaryText, fontWeight: 600 }}>{jobName}</Box>
                {jobName && worker.organization ? ' · ' : ''}{worker.organization}
              </Typography>
              {summaryEmail && <Stack direction="row" spacing={0.75} sx={{ mt: 1, alignItems: 'flex-start', minWidth: 0 }}>
                <MailOutlined aria-hidden sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{summaryEmail}</Typography>
              </Stack>}
            </Box>
          </Stack>
          <Box data-testid="worker-profile-summary" sx={{ bgcolor: designTokens.color.overviewSurface, borderRadius: designTokens.radius.control, p: 2, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0, mb: 1.25 }}>인사 기준일 · {asOf}</Typography>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
              {summaryItems.map(({ label, value }) => <Box key={label} sx={{ minWidth: 0 }}>
                <Typography component="dt" variant="caption" color="text.secondary">{label}</Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.5, fontWeight: 500, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{value}</Typography>
              </Box>)}
            </Box>
          </Box>
        </Box>}
      />
    </Box>

    <PillTabs appearance="capsule" value={tab} items={profileTabs} ariaLabel="구성원 상세 메뉴" onChange={setTab} />

    <WorkerProfilePanel tab="overview" active={tab === 'overview'}>
      <Stack spacing={2}>
        <SectionCard
          title={<Stack spacing={0.5}>{profileSectionTitle('worker-profile-basic-title', <BadgeOutlined aria-hidden sx={{ fontSize: 20, color: 'text.secondary' }} />, '기본 정보')}<Typography variant="caption" color="text.secondary">현재 정보</Typography></Stack>}
          action={onEditName ? <CompactEditButton aria-label="이름 변경" onClick={onEditName}>이름 변경</CompactEditButton> : undefined}
          bodyPadding="standard"
        >
          <Box>
            <DefinitionGrid testId="worker-name-information" columns={4} items={[
              { id: 'legal-name', label: '이름', value: nameValue(legalName) },
              { id: 'legal-english-name', label: '영문 이름', value: nameValue(nameProfile?.legalEnglishName?.fullName.trim()) },
              { id: 'preferred-name', label: '선호 이름', value: nameValue(preferredName) },
              { id: 'preferred-english-name', label: '영문 선호 이름', value: nameValue(nameProfile?.preferredEnglishName?.fullName.trim()) },
              { id: 'birth-date', label: '생일', value: privateFieldValue(Boolean(privateProfile?.canReadPersonalData), profileDateValue(privateProfile?.birthDate), false, privateProfile?.personalDataLoaded, privateLoading) },
              { id: 'gender', label: '성별', value: privateFieldValue(Boolean(privateProfile?.canReadPersonalData), genderValue(privateProfile?.gender), false, privateProfile?.personalDataLoaded, privateLoading) },
              { id: 'marital-status', label: '혼인 상태', value: privateFieldValue(Boolean(privateProfile?.canReadPersonalData), maritalStatusValue(privateProfile?.maritalStatus), false, privateProfile?.personalDataLoaded, privateLoading) },
            ]} />
          </Box>
        </SectionCard>

        <SectionCard
          title={<Stack spacing={0.5}>{profileSectionTitle('worker-profile-contact-title', <ContactPhoneOutlined aria-hidden sx={{ fontSize: 20, color: 'text.secondary' }} />, '연락처 정보')}<Typography variant="caption" color="text.secondary">현재 정보</Typography></Stack>}
          help="변경 사항은 승인 후 시행일에 반영됩니다. 개인 연락처는 권한에 따라 마스킹되며, 원문 조회 시 감사 기록이 남습니다."
          bodyPadding="standard"
        >
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
              <ProfileGroup title="회사 연락처" action={canEditWork ? <CompactEditButton aria-label="회사 연락처 변경 요청" onClick={() => onEditContact?.('WORK')}>변경 요청</CompactEditButton> : undefined}>
                <DefinitionGrid testId="worker-work-contact" items={workContactItems} />
              </ProfileGroup>
              <ProfileGroup title="개인 연락처" action={canEditHome ? <CompactEditButton aria-label="개인 연락처 변경 요청" onClick={() => onEditContact?.('HOME')}>변경 요청</CompactEditButton> : undefined}>
                <DefinitionGrid testId="worker-home-contact" items={homeContactItems} />
              </ProfileGroup>
            </Box>
            <Box data-testid="worker-emergency-contacts" sx={{ mt: 2.25 }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 1 }}>
                <Typography component="h4" variant="body2" sx={{ fontWeight: 600 }}>
                  비상 연락처 · {!canReadEmergency
                    ? '조회 권한 없음'
                    : privateLoading ? '불러오는 중' : privateProfile?.emergencyContactsLoaded
                      ? `${privateProfile?.emergencyContacts?.length ?? 0}명`
                      : '불러오기 실패'}
                </Typography>
                {canEditEmergency && <CompactEditButton aria-label="비상 연락처 변경 요청" onClick={() => onEditContact?.('EMERGENCY')}>변경 요청</CompactEditButton>}
              </Stack>
              {canReadEmergency && privateProfile?.emergencyContactsLoaded ? privateProfile.emergencyContacts?.length ? <Stack spacing={1}>
                {privateProfile.emergencyContacts.map((contact, index) => <Box
                  key={contact.id}
                  data-testid={`worker-emergency-contact-${index}`}
                  sx={{ p: 1.5, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.overviewSurface }}
                >
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 760 }}>{contact.name || contact.descriptor || '이름 미등록'}</Typography>
                    {contact.relationshipType ? <ValueBadge label={emergencyRelationshipValue(contact.relationshipType)} /> : <Typography variant="caption" color="text.secondary">관계 미등록</Typography>}
                    <StatusBadge tone={index === 0 ? 'info' : 'neutral'} label={`${contact.priority || index + 1}순위`} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                    {(contact.phoneNumbers ?? []).map((phone) => [phoneTypeLabels[phone.usageType ?? ''] || phone.usageType, phone.phoneNumber].filter(Boolean).join(' ')).join(' · ') || '전화번호 없음'}
                  </Typography>
                  {!!contact.emailAddresses?.length && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
                    {contact.emailAddresses.map((email) => email.emailAddress).filter(Boolean).join(' · ')}
                  </Typography>}
                </Box>)}
              </Stack> : <Typography variant="body2" color="text.secondary">등록된 비상 연락처가 없습니다.</Typography> : null}
            </Box>
          </Box>
        </SectionCard>
      </Stack>
    </WorkerProfilePanel>

    <WorkerProfilePanel tab="employment" active={tab === 'employment'}>
      <SectionCard
        title={<Stack spacing={0.5}>
          {profileSectionTitle('worker-profile-hr-title', <AccountTreeOutlined aria-hidden sx={{ fontSize: 20, color: 'text.secondary' }} />, '인사 정보')}
          <Typography variant="caption" color="text.secondary">조회 기준일 · {asOf}</Typography>
        </Stack>}
        action={personnelActions ?? (onStartStaffing ? <Button size="small" variant="outlined" onClick={onStartStaffing}>이 구성원 발령</Button> : undefined)}
        bodyPadding="standard"
      >
        <Stack spacing={3}>
          <ProfileGroup title="소속 조직" action={onOpenOrganizationStaffing ? <Button size="small" variant="outlined" endIcon={<ArrowForwardOutlined />} onClick={onOpenOrganizationStaffing}>조직 발령</Button> : undefined}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid="worker-organization-membership-primary"><Typography variant="body2">{profileTextValue(worker.organization)}</Typography><ValueBadge label="주 소속" /></Stack>
              {worker.additionalJobs.map(job => <Stack key={job.id} direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid={`worker-organization-membership-${job.id}`}><Typography variant="body2">{job.organization}</Typography><ValueBadge label="추가 소속" /></Stack>)}
            </Stack>
          </ProfileGroup>
          <ProfileGroup title="직무·근무 정보">
            <DefinitionGrid testId="worker-job-information" columns={3} items={[
              { id: 'job', label: '직무', value: profileTextValue(jobName) },
              ...(worker.jobFamily ? [{ id: 'job-family', label: '직군', value: worker.jobFamily }] : []),
              ...(worker.jobLevel ? [{ id: 'job-level', label: '직무 레벨', value: worker.jobLevel }] : []),
              { id: 'business-title', label: '직함', value: profileTextValue(worker.businessTitle) },
              { id: 'manager', label: '보고 관리자', value: profileTextValue(worker.manager) },
              { id: 'location', label: '근무지', value: profileTextValue(worker.location) },
              { id: 'work-space', label: '업무 공간', value: profileTextValue(worker.workSpace) },
            ]} />
          </ProfileGroup>
          <ProfileGroup title="고용·계약">
            <DefinitionGrid testId="worker-hr-information" columns={3} items={[
              { id: 'company', label: '법적 소속 회사', value: profileTextValue(worker.company) },
              { id: 'worker-type', label: '구성원 유형', value: profileTextValue(workerTypeDisplay) },
              { id: 'job-type', label: jobTypeLabel, value: profileTextValue(worker.jobType) },
              { id: 'time-type', label: '근무 형태', value: worker.timeType ? timeTypeLabels[worker.timeType] : undefined },
              ...(isEmployee ? [{ id: 'contract-type', label: '근로계약 유형', value: privateFieldValue(Boolean(employmentProfile?.canReadContract), employmentProfile?.contractType ? contractTypeLabels[employmentProfile.contractType] || employmentProfile.contractType : undefined, false, !employmentError, employmentLoading) }] : []),
            ]} />
          </ProfileGroup>
          <ProfileGroup title="입퇴사">
            <DefinitionGrid testId="worker-employment-dates" columns={3} items={[
              { id: 'hire-date', label: '최근 입사일', value: profileDateValue(worker.hireDate) },
              { id: 'original-hire-date', label: '최초 입사일', value: profileDateValue(worker.originalHireDate || worker.hireDate) },
              { id: 'termination-date', label: '종료 예정일', value: profileDateValue(worker.terminationDate) },
            ]} />
          </ProfileGroup>
        </Stack>
      </SectionCard>
    </WorkerProfilePanel>

    {familyContent && <WorkerProfilePanel tab="family" active={tab === 'family'}>{familyContent}</WorkerProfilePanel>}

    {careerContent && <WorkerProfilePanel tab="career" active={tab === 'career'}>{careerContent}</WorkerProfilePanel>}

    <WorkerProfilePanel tab="history" active={tab === 'history'}>{historyContent ?? <SectionCard
      title={profileSectionTitle('worker-profile-history-title', <HistoryOutlined aria-hidden sx={{ fontSize: 20, color: 'text.secondary' }} />, `변경 이력 ${workerChanges.length}`)}
      bodyPadding="standard"
    ><Box>
      {workerChanges.length ? <Stack spacing={0}>
        {workerChanges.map((change, index) => {
          const tone = dateTone(change.effectiveDate || change.createdAt.slice(0, 10), asOf);
          return <Stack key={change.id} direction="row" spacing={1.25} sx={{ minHeight: 74 }}>
            <Stack sx={{ alignItems: 'center' }}><Avatar sx={{ width: 28, height: 28, bgcolor: index === 0 ? designTokens.color.action : designTokens.color.divider, color: index === 0 ? designTokens.color.inverseText : designTokens.color.secondaryText }}><HistoryOutlined sx={{ fontSize: 16 }} /></Avatar>{index < workerChanges.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: designTokens.color.divider }} />}</Stack>
            <Box sx={{ flex: 1, pb: 2 }}><Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}><Typography variant="body2" sx={{ fontWeight: 760 }}>{processLabels[change.processType] || change.descriptor}</Typography><StatusBadge tone={tone.color} label={tone.label} /></Stack><Typography variant="caption" color="text.secondary">시행일 {profileDateValue(change.effectiveDate) || '미입력'} · <StatusBadge label={change.status} /></Typography>{change.correctionOf && <Typography variant="caption" component="div" color="warning.main">정정 원본 {change.correctionOf.descriptor || '원본 변경 건'}</Typography>}</Box>
          </Stack>;
        })}
      </Stack> : <Alert severity="info">이 구성원에게 연결된 발령 이력이 없습니다.</Alert>}
    </Box></SectionCard>}</WorkerProfilePanel>
  </Stack>;
}

function WorkerProfilePanel({ tab, active, children }: { tab: WorkerProfileTab; active: boolean; children: ReactNode }) {
  return <Box
    id={`worker-profile-${tab}-panel`}
    role="tabpanel"
    aria-labelledby={`worker-profile-${tab}-tab`}
    hidden={!active}
  >{active ? children : null}</Box>;
}
